import React, { useEffect, useState } from 'react'
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Row,
  Col,
  DatePicker,
  Select,
  Upload,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  FileTextOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import {
  CreateEmployeeData,
  GetEmployeeData,
  UpdateEmployeeData,
  DeleteEmployeeData,
} from "../../API's/AdminApi"
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import { EmpImgPath } from "../../API's/UserApi"

const { Option } = Select

export default function EmployeeMaster() {
  const [data, setData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editData, setEditData] = useState(null)
  const [deleteId, setDeleteId] = useState(null)

  const [tableLoading, setTableLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [fileList, setFileList] = useState([])
    const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  const [form] = Form.useForm()

  const designationMap = {
    1: 'Trainer',
    2: 'Staff',
    3: 'Ticket Collector',
    4: 'Life Guard',
    5: 'Cleaner',
  }

  // ---------------- GET DATA ----------------
  const getEmployees = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetEmployeeData)
      setData(res.data || [])
    } catch (error) {
      toast.error('Failed to load employees')
    } finally {
      setTableLoading(false)
    }
  }

  useEffect(() => {
    getEmployees()
  }, [])

  // ---------------- ADD / UPDATE ----------------
  const onFinish = async (values) => {
    try {
      setSubmitLoading(true)

      const formData = new FormData()

      //  Correct field names
      formData.append('employeeName', values.employeeName || '')
      formData.append('mobileNo', values.mobileNo || '')
      formData.append('empDesignation', values.empDesignation || '')
      formData.append('gender', values.gender || '')
      formData.append('address', values.address || '')
      formData.append('aadhaarNo', values.aadhaarNo || '')
      formData.append('biometricId', values.biometricId || '')

      formData.append('dob', values.dob ? values.dob.format('YYYY-MM-DD') : '')
      formData.append(
        'joiningDate',
        values.joiningDate ? values.joiningDate.format('YYYY-MM-DD') : '',
      )
      formData.append(
        'validUpToDate',
        values.validUpToDate ? values.validUpToDate.format('YYYY-MM-DD') : '',
      )

      //  FILE FIX
      if (fileList.length > 0 && fileList[0].originFileObj) {
        // new uploaded file
        formData.append('photo', fileList[0].originFileObj)
      }

      let res

      if (editData) {
        formData.append('id', editData.id)

        res = await axiosInstance.put(UpdateEmployeeData, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (res.data === 'UPDATED') {
          toast.success('Employee updated successfully')
        }
      } else {
        res = await axiosInstance.post(CreateEmployeeData, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (res.data === 'SAVED') {
          toast.success('Employee created successfully')
        }

        if (res.data === 'ALREADYEXISTS') {
          toast.warn('Employee mobile no. already exist')
          return
        }
      }

      // RESET
      resetFormState()
      setShowForm(false)
      getEmployees()
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong')
    } finally {
      setSubmitLoading(false)
    }
  }

  // ---------------- EDIT ----------------
  const handleEdit = (record) => {
    setEditData(record)
    setShowForm(true)

    const imageUrl = record.photo ? `${EmpImgPath}${record.photo}` : ''

    const files = record.photo
      ? [
          {
            uid: '-1',
            name: record.photo,
            status: 'done',
            url: imageUrl,
          },
        ]
      : []

    setFileList(files)
    setPreviewImage(imageUrl)

    form.setFieldsValue({
      ...record,
      dob: record.dob ? dayjs(record.dob) : null,
      joiningDate: record.joiningDate ? dayjs(record.joiningDate) : null,
      validUpToDate: record.validUpToDate ? dayjs(record.validUpToDate) : null,
      file: files,
    })
  }

  // ---------------- DELETE ----------------
  const confirmDelete = async () => {
    try {
      setDeleteLoading(true)

      const res = await axiosInstance.delete(
        `${DeleteEmployeeData}?id=${deleteId}`,
      )

      if (res.data) {
        toast.success(res.data.message || 'Deleted successfully')
        setDeleteId(null)
        getEmployees()
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Delete failed')
    } finally {
      setDeleteLoading(false)
    }
  }

  // ---------------- TABLE ----------------
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'employeeName',
    },
    {
      title: 'Mobile',
      dataIndex: 'mobileNo',
    },
    {
      title: 'Designation',
      dataIndex: 'empDesignation',
      render: (value) => designationMap[value] || '-',
    },
    {
      title: 'Action',
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ marginRight: 8 }}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setDeleteId(record.id)}
          />
        </>
      ),
    },
  ]

  // ---------------- SEARCH ----------------
  const filteredData = data.filter((item) =>
    item.employeeName?.toLowerCase().includes(searchText.toLowerCase()),
  )

  const resetFormState = () => {
    form.resetFields()
    setFileList([])
    setPreviewImage('')
    setShowForm(false)
    setEditData(null)
  }

  return (
    <div>
      {/* TABLE */}
      {!showForm && (
        <>
          <div className="page-background p-2 p-md-4">
            <div className="page-header">
              <h2>
                <UserOutlined style={{ marginRight: 8 }} />
                Employee Master
              </h2>
            </div>
            <Row gutter={16} className="pb-2 m-0">
              <Col xs={24} md={6} xl={14}></Col>

              <Col xs={24} md={12} xl={6} className="pt-2 pt-md-0">
                <Input
                  placeholder="Search here"
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Col>

              <Col xs={24} md={6} xl={4} className="pt-2 pt-md-0 mt-1">
                <button
                  className="btn-aqua"
                  onClick={() => {
                    resetFormState()
                    setShowForm(true)
                  }}
                >
                  <PlusOutlined /> Add Employee
                </button>
              </Col>
            </Row>

            <Table
              className="custom-table-header shadow"
              columns={columns}
              dataSource={filteredData}
              rowKey="id"
              loading={tableLoading}
              bordered
              style={{ overflowX: 'auto', backgroundColor: 'white' }}
              pagination={{
                showSizeChanger: true, // enable page size dropdown
                pageSizeOptions: ['5', '10', '20', '50'],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </div>
        </>
      )}

      {/* FORM */}
      {showForm && (
        <div className="row p-0 m-0">
          <div className="col-12 col-lg-1 "></div>
          <div className="col-12 col-lg-10  page-background1 p-2 p-md-4">
            {/* HEADER */}
            <div className="page-header">
              <h2>
                <UserOutlined style={{ marginRight: 8 }} />
                Employee Master
              </h2>
            </div>
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Row gutter={16}>
                <Col xs={24} md={16}>
                  <Row gutter={16}>
                    <Col xs={24} lg={20}>
                      <Form.Item
                        name="employeeName"
                        label="Employee Name"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={4}></Col>

                    <Col xs={24} md={12} lg={8}>
                      <Form.Item name="dob" label="DOB">
                        <DatePicker
                          style={{ width: '100%' }}
                          size="large"
                          format="DD-MM-YYYY"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={8}>
                      <Form.Item
                        name="mobileNo"
                        label="Mobile No"
                        rules={[
                          { required: true, message: 'Enter mobile number' },
                          {
                            pattern: /^[0-9]{10}$/,
                            message: 'Mobile must be 10 digits',
                          },
                        ]}
                      >
                        <Input
                          maxLength={10}
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(
                              /[^0-9]/g,
                              '',
                            )
                          }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={8}>
                      <Form.Item name="gender" label="Gender">
                        <Select>
                          <Option value={1}>Male</Option>
                          <Option value={2}>Female</Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={24}></Col>

                    <Col xs={24} md={24}>
                      <Form.Item name="address" label="Address">
                        <Input.TextArea rows={2} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={8}>
                      <Form.Item
                        name="aadhaarNo"
                        label="Aadhar No"
                        rules={[
                          { required: true, message: 'Enter Aadhar number' },
                          {
                            pattern: /^[0-9]{12}$/,
                            message: 'Aadhar must be 12 digits',
                          },
                        ]}
                      >
                        <Input
                          maxLength={12}
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(
                              /[^0-9]/g,
                              '',
                            )
                          }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={8}>
                      <Form.Item
                        name="empDesignation"
                        label="Employee Designation"
                        rules={[
                          {
                            required: true,
                            message: 'Please select designation',
                          },
                        ]}
                      >
                        <Select placeholder="Select Designation">
                          <Option value="1">Trainer</Option>
                          <Option value="2">Staff</Option>
                          <Option value="3">Ticket Collector</Option>
                          <Option value="4">Life Guard</Option>
                          <Option value="5">Cleaner</Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={8}>
                      <Form.Item name="joiningDate" label="Joining Date">
                        <DatePicker
                          style={{ width: '100%' }}
                          size="large"
                          format="DD-MM-YYYY"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={8}>
                      <Form.Item name="validUpToDate" label="Valid Upto Date">
                        <DatePicker
                          style={{ width: '100%' }}
                          size="large"
                          format="DD-MM-YYYY"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12} lg={8}>
                      <Form.Item name="biometricId" label="Biometric ID">
                        <Input
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(
                              /[^0-9]/g,
                              '',
                            )
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="file"
                    label="Upload Photo"
                    rules={[
                      { required: !editData, message: 'Please upload photo' },
                    ]}
                  >
                    <Upload
                      fileList={fileList}
                      onChange={({ fileList }) => {
                        setFileList(fileList)

                        // Set preview
                        if (fileList.length > 0) {
                          const file = fileList[0].originFileObj
                          const reader = new FileReader()
                          reader.readAsDataURL(file)
                          reader.onload = () => {
                            setPreviewImage(reader.result)
                          }
                        } else {
                          setPreviewImage('')
                        }

                        // 🔥 IMPORTANT: Update form value manually
                        form.setFieldsValue({ file: fileList })
                      }}
                      beforeUpload={(file) => {
                        const isValidType =
                          file.type === 'image/jpeg' ||
                          file.type === 'image/jpg' ||
                          file.type === 'image/png'

                        if (!isValidType) {
                          toast.error('Only JPG, JPEG, PNG files allowed')
                          return Upload.LIST_IGNORE
                        }

                        const isLt3MB = file.size / 1024 / 1024 < 3

                        if (!isLt3MB) {
                          toast.error('Image must be smaller than 3MB')
                          return Upload.LIST_IGNORE
                        }

                        return false // prevent auto upload
                      }}
                      onRemove={() => {
                        setFileList([])
                        setPreviewImage('')
                        form.setFieldsValue({ file: [] }) // 🔥 important
                      }}
                      maxCount={1}
                      listType="picture"
                    >
                      <Button icon={<UploadOutlined />}>Select Photo</Button>
                    </Upload>

                    {previewImage && (
                      <div style={{ marginTop: 10 }}>
                        <img
                          src={previewImage}
                          alt="Preview"
                          style={{
                            width: '100%',
                            maxHeight: 250,
                            objectFit: 'fill',
                            borderRadius: 8,
                            border: '1px solid #ddd',
                          }}
                        />
                      </div>
                    )}
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitLoading}
                >
                  {editData ? 'Update' : 'Submit'}
                </Button>

                <Button
                  style={{ marginLeft: 10 }}
                  onClick={() => {
                    resetFormState()
                    setShowForm(false)
                  }}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          </div>
          <div className="col-12  col-lg-1 "></div>
        </div>
      )}

      {/* DELETE MODAL */}
      <Modal
        title="Confirm Delete"
        open={deleteId !== null}
        onOk={confirmDelete}
        confirmLoading={deleteLoading}
        onCancel={() => setDeleteId(null)}
      >
        Are you sure you want to delete this employee?
      </Modal>
    </div>
  )
}
